import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { 
  ArrowRight, 
  GraduationCap, 
  Camera, 
  BookOpen, 
  Mic, 
  Video,
  Users,
  Globe,
  Zap,
  Star,
  Play,
  Download,
  TrendingUp,
  Award,
  Instagram,
  Gamepad2
} from "lucide-react";

const MediaPage = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.8, ease: "easeOut" }
  };

  const staggerContainer = {
    initial: {},
    animate: { transition: { staggerChildren: 0.1 } }
  };

  const services = [
    {
      icon: <Users className="w-8 h-8" />,
      title: "Creator Development Team",
      description: "5-person media team specializing in brand building, with 2 members already earning $2,000+ monthly from their personal brands."
    },
    {
      icon: <Instagram className="w-8 h-8" />,
      title: "Social Media Empire", 
      description: "15 owned Instagram accounts across diverse niches, collectively building 250,000+ followers and creating multiple revenue streams."
    },
    {
      icon: <BookOpen className="w-8 h-8" />,
      title: "Educational Content Development",
      description: "4 branded content pages across different niches, with free e-books in development to educate and attract new audiences."
    },
    {
      icon: <Gamepad2 className="w-8 h-8" />,
      title: "Interactive Learning Platforms",
      description: "Nonprofit-sponsored online game in development, making wealth education engaging and accessible for younger audiences."
    }
  ];

  const metrics = [
    { number: "250K+", label: "Total Followers" },
    { number: "15", label: "Instagram Accounts" },
    { number: "5", label: "Team Members" },
    { number: "4", label: "Content Brands" }
  ];

  const achievements = [
    {
      title: "Team Monetization Success",
      description: "2 team members earning $2,000+ monthly",
      metric: "$2K+",
      icon: <TrendingUp className="w-6 h-6" />
    },
    {
      title: "Social Media Reach",
      description: "250,000+ followers across platforms",
      metric: "250K+",
      icon: <Instagram className="w-6 h-6" />
    },
    {
      title: "Creator Support",
      description: "Helped smaller creators monetize and grow",
      metric: "Proven",
      icon: <Award className="w-6 h-6" />
    },
    {
      title: "Content Diversity",
      description: "4 branded content pages across niches",
      metric: "4",
      icon: <Globe className="w-6 h-6" />
    }
  ];

  const projects = [
    {
      title: "Creator Monetization Program",
      status: "Active",
      description: "Helping team members and external creators build sustainable income streams through content creation",
      results: "2 members earning $2,000+ monthly"
    },
    {
      title: "Multi-Niche Content Network",
      status: "Expanding", 
      description: "15 Instagram accounts across diverse niches building engaged audiences and revenue opportunities",
      results: "250,000+ collective followers"
    },
    {
      title: "Educational E-Books",
      status: "Development",
      description: "Free educational resources to attract audiences and establish thought leadership across sectors",
      results: "Multiple titles in production"
    },
    {
      title: "Nonprofit Learning Game",
      status: "Development",
      description: "Interactive online game teaching wealth-building concepts to youth, sponsored by the Nhalege Foundation",
      results: "Beta testing phase"
    }
  ];

  return (
    <div className="bg-black text-white min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-24 overflow-hidden">
        {/* Sacred Geometry Background */}
        <div className="absolute inset-0 opacity-8">
          <motion.div 
            className="absolute inset-0"
            animate={{ rotate: 360 }}
            transition={{ duration: 200, repeat: Infinity, ease: "linear" }}
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='120' height='120' viewBox='0 0 120 120' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' stroke='%23f59e0b' stroke-opacity='0.1' stroke-width='1'%3E%3Cpath d='M60 10L105 35v50L60 110L15 85V35z'/%3E%3Cpath d='M60 25L90 42.5v35L60 95L30 77.5v-35z'/%3E%3C/g%3E%3C/svg%3E")`,
              backgroundSize: '120px 120px'
            }}
          />
        </div>

        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1620794511798-d7ba5299a087"
            alt="Media and content creation"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-black/80 via-black/60 to-blue-900/40"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center"
            initial="initial"
            animate="animate"
            variants={staggerContainer}
          >
            <motion.div 
              className="flex justify-center mb-8"
              variants={fadeInUp}
            >
              <div className="w-24 h-24 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full flex items-center justify-center shadow-2xl shadow-blue-400/50">
                <GraduationCap className="w-12 h-12 text-black" />
              </div>
            </motion.div>

            <motion.h1 
              className="text-6xl md:text-8xl font-bold mb-6"
              variants={fadeInUp}
            >
              Nhalege <span className="text-blue-400">Media & Education</span>
            </motion.h1>
            
            <motion.p 
              className="text-2xl md:text-3xl text-blue-400 mb-8 font-light"
              variants={fadeInUp}
            >
              Content is Currency
            </motion.p>
            
            <motion.p 
              className="text-xl text-white/80 max-w-4xl mx-auto mb-12 leading-relaxed"
              variants={fadeInUp}
            >
              Building a media empire with 250,000+ followers across 15 Instagram accounts. Our 5-person team 
              is not only creating content but monetizing their expertise, with 2 members already earning $2,000+ monthly 
              while helping other creators build sustainable income streams.
            </motion.p>

            <motion.div 
              className="flex flex-col sm:flex-row gap-6 justify-center"
              variants={fadeInUp}
            >
              <Link 
                to="/contact"
                className="group bg-gradient-to-r from-blue-400 to-purple-500 text-black px-12 py-4 text-lg font-bold rounded-full hover:shadow-2xl hover:shadow-blue-400/40 transition-all duration-300 hover:scale-105"
              >
                <div className="flex items-center space-x-2">
                  <span>Join Our Network</span>
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
              
              <Link 
                to="/contact"
                className="group border-2 border-blue-400 text-blue-400 px-12 py-4 text-lg font-bold rounded-full hover:bg-blue-400 hover:text-black transition-all duration-300 hover:scale-105"
              >
                <div className="flex items-center space-x-2">
                  <Users className="w-5 h-5" />
                  <span>Creator Support</span>
                </div>
              </Link>
              
              <Link 
                to="/contact"
                className="group border-2 border-white/30 text-white px-12 py-4 text-lg font-bold rounded-full hover:bg-white/10 transition-all duration-300 hover:scale-105"
              >
                <div className="flex items-center space-x-2">
                  <Play className="w-5 h-5" />
                  <span>Free Resources</span>
                </div>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Content Metrics Section */}
      <section className="py-16 bg-gradient-to-r from-gray-900 to-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="grid grid-cols-2 md:grid-cols-4 gap-8"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            {metrics.map((metric, index) => (
              <motion.div 
                key={metric.label}
                className="text-center"
                variants={fadeInUp}
              >
                <div className="text-4xl md:text-5xl font-bold text-blue-400 mb-2">
                  {metric.number}
                </div>
                <div className="text-white/70 text-sm font-medium tracking-wider uppercase">
                  {metric.label}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Core Mission Section */}
      <section className="py-24 bg-black relative">
        <div className="absolute inset-0 opacity-5">
          <img 
            src="https://images.pexels.com/photos/7098114/pexels-photo-7098114.jpeg"
            alt="Content creation team"
            className="w-full h-full object-cover"
          />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <motion.div 
            className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.div variants={fadeInUp}>
              <h2 className="text-5xl md:text-6xl font-bold mb-8">
                Media <span className="text-blue-400">Empire</span>
              </h2>
              <div className="space-y-6 text-lg text-white/80 leading-relaxed">
                <p>
                  Nhalege Media & Education operates as the knowledge-sharing engine of our empire, 
                  with a 5-person team building sustainable income streams through strategic content creation.
                </p>
                <p>
                  Our network of 15 Instagram accounts has generated 250,000+ followers across diverse niches, 
                  proving that authentic content creation can build both audience and revenue simultaneously.
                </p>
                <p>
                  We've successfully <span className="text-blue-400 font-semibold">
                  helped smaller creators monetize their expertise</span> while building our own media empire 
                  that educates, entertains, and generates sustainable income.
                </p>
              </div>
              
              <div className="mt-10 p-8 bg-gradient-to-br from-blue-400/10 to-transparent border border-blue-400/20 rounded-2xl">
                <h3 className="text-2xl font-bold text-blue-400 mb-4">Content Strategy</h3>
                <ul className="space-y-3 text-white/80">
                  <li className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                    <span>Multi-platform presence across diverse niches</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                    <span>Team member monetization and skill development</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                    <span>Educational content that builds authority</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                    <span>Creator support and monetization guidance</span>
                  </li>
                </ul>
              </div>
            </motion.div>

            <motion.div 
              className="relative"
              variants={fadeInUp}
            >
              <div className="relative">
                <img 
                  src="https://images.pexels.com/photos/8108531/pexels-photo-8108531.jpeg"
                  alt="Team monetization"
                  className="w-full h-96 object-cover rounded-3xl shadow-2xl"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent rounded-3xl"></div>
                
                {/* Floating Success Card */}
                <div className="absolute -bottom-6 -right-6 bg-gradient-to-br from-gray-900 to-black border border-blue-400/30 rounded-2xl p-6 backdrop-blur-lg">
                  <div className="flex items-center space-x-4">
                    <TrendingUp className="w-8 h-8 text-blue-400" />
                    <div>
                      <div className="text-2xl font-bold text-white">$2K+</div>
                      <div className="text-sm text-white/60">Monthly Earnings</div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-gradient-to-br from-gray-900 to-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-16"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h2 className="text-5xl md:text-6xl font-bold mb-8">
              Content <span className="text-blue-400">Ecosystem</span>
            </h2>
            <p className="text-xl text-white/80 max-w-3xl mx-auto">
              Four core areas of our media and education empire driving growth and monetization
            </p>
          </motion.div>

          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 gap-8"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                className="group bg-gradient-to-br from-gray-900/50 to-black/50 backdrop-blur-lg border border-white/10 rounded-2xl p-8 hover:border-blue-400/50 transition-all duration-500"
                variants={fadeInUp}
                whileHover={{ y: -5 }}
              >
                <div className="flex items-start space-x-6">
                  <div className="flex-shrink-0">
                    <div className="w-16 h-16 bg-gradient-to-br from-blue-400 to-purple-500 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <span className="text-black">{service.icon}</span>
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-blue-400 transition-colors duration-300">
                      {service.title}
                    </h3>
                    <p className="text-white/70 leading-relaxed">
                      {service.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Active Projects Section */}
      <section className="py-24 bg-black relative">
        <div className="absolute inset-0 opacity-10">
          <motion.div 
            animate={{ x: [0, -100, 0] }}
            transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' stroke='%23f59e0b' stroke-opacity='0.1' stroke-width='1'%3E%3Cpath d='M50 5L85 25v50L50 95L15 75V25z'/%3E%3Cpath d='M50 20L70 32.5v35L50 80L30 67.5v-35z'/%3E%3C/g%3E%3C/svg%3E")`,
              backgroundSize: '100px 100px'
            }}
          />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <motion.div 
            className="text-center mb-16"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h2 className="text-5xl md:text-6xl font-bold mb-8">
              Active <span className="text-blue-400">Projects</span>
            </h2>
            <p className="text-xl text-white/80 max-w-3xl mx-auto">
              Current initiatives building our media empire and supporting creator monetization
            </p>
          </motion.div>

          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 gap-8"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            {projects.map((project, index) => (
              <motion.div
                key={project.title}
                className="group bg-gradient-to-br from-gray-900/80 to-black/80 backdrop-blur-lg border border-blue-400/20 rounded-3xl p-8 hover:border-blue-400/50 transition-all duration-500"
                variants={fadeInUp}
                whileHover={{ y: -5, scale: 1.02 }}
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-2xl font-bold text-white group-hover:text-blue-400 transition-colors duration-300">
                    {project.title}
                  </h3>
                  <div className={`px-3 py-1 rounded-full text-sm font-bold ${
                    project.status === 'Active' ? 'bg-green-400 text-black' :
                    project.status === 'Expanding' ? 'bg-blue-400 text-black' :
                    'bg-orange-400 text-black'
                  }`}>
                    {project.status}
                  </div>
                </div>
                
                <p className="text-white/70 mb-6 leading-relaxed">
                  {project.description}
                </p>
                
                <div className="mt-4 p-4 bg-blue-400/10 border border-blue-400/20 rounded-lg">
                  <div className="text-blue-400 font-semibold text-sm mb-2">Results:</div>
                  <div className="text-white/80">{project.results}</div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Achievements Section */}
      <section className="py-24 bg-gradient-to-br from-gray-900 to-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-16"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h2 className="text-5xl md:text-6xl font-bold mb-8">
              Key <span className="text-blue-400">Achievements</span>
            </h2>
            <p className="text-xl text-white/80 max-w-3xl mx-auto">
              Measurable results that demonstrate our media and education impact
            </p>
          </motion.div>

          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            {achievements.map((achievement, index) => (
              <motion.div
                key={achievement.title}
                className="group text-center bg-gradient-to-br from-gray-900/50 to-black/50 backdrop-blur-lg border border-blue-400/20 rounded-2xl p-8 hover:border-blue-400/50 transition-all duration-500"
                variants={fadeInUp}
                whileHover={{ scale: 1.05 }}
              >
                <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full flex items-center justify-center group-hover:shadow-lg group-hover:shadow-blue-400/30 transition-all duration-300">
                  <span className="text-black">{achievement.icon}</span>
                </div>
                <h3 className="text-xl font-bold text-white mb-2 group-hover:text-blue-400 transition-colors duration-300">
                  {achievement.title}
                </h3>
                <div className="text-3xl font-bold text-blue-400 mb-2">{achievement.metric}</div>
                <p className="text-white/70 text-sm">{achievement.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-24 bg-gradient-to-br from-blue-400/10 via-black to-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.h2 
              className="text-5xl md:text-6xl font-bold mb-8"
              variants={fadeInUp}
            >
              Join Our <span className="text-blue-400">Network</span>
            </motion.h2>
            
            <motion.p 
              className="text-xl text-white/80 mb-12"
              variants={fadeInUp}
            >
              Whether you're a content creator looking to monetize your skills or a brand seeking authentic 
              media partnerships, join our proven system that helps creators build sustainable income.
            </motion.p>
            
            <motion.div 
              className="grid grid-cols-1 md:grid-cols-2 gap-8"
              variants={fadeInUp}
            >
              <div className="bg-gradient-to-br from-gray-900/50 to-black/50 backdrop-blur-lg border border-blue-400/20 rounded-2xl p-8">
                <h3 className="text-2xl font-bold text-blue-400 mb-6">Creator Application</h3>
                <div className="space-y-4">
                  <input 
                    type="text" 
                    placeholder="Your Name"
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:border-blue-400"
                  />
                  <input 
                    type="text" 
                    placeholder="Content Niche"
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:border-blue-400"
                  />
                  <input 
                    type="text" 
                    placeholder="Current Follower Count"
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:border-blue-400"
                  />
                  <Link 
                    to="/contact"
                    className="w-full bg-gradient-to-r from-blue-400 to-purple-500 text-black py-3 font-bold rounded-full hover:shadow-lg hover:shadow-blue-400/30 transition-all duration-300 hover:scale-105 flex items-center justify-center"
                  >
                    Apply to Join Network
                  </Link>
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-gray-900/50 to-black/50 backdrop-blur-lg border border-blue-400/20 rounded-2xl p-8">
                <h3 className="text-2xl font-bold text-blue-400 mb-6">Brand Partnership</h3>
                <div className="space-y-4">
                  <input 
                    type="text" 
                    placeholder="Company Name"
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:border-blue-400"
                  />
                  <input 
                    type="email" 
                    placeholder="Contact Email"
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:border-blue-400"
                  />
                  <select className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-blue-400">
                    <option value="">Partnership Type</option>
                    <option value="content">Content Creation</option>
                    <option value="influencer">Influencer Marketing</option>
                    <option value="education">Educational Content</option>
                    <option value="consulting">Media Consulting</option>
                  </select>
                  <Link 
                    to="/contact"
                    className="w-full bg-gradient-to-r from-blue-400 to-purple-500 text-black py-3 font-bold rounded-full hover:shadow-lg hover:shadow-blue-400/30 transition-all duration-300 hover:scale-105 flex items-center justify-center"
                  >
                    Explore Partnership
                  </Link>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Navigation Footer */}
      <section className="py-16 bg-gray-900 border-t border-blue-400/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <Link 
              to="/foundation"
              className="group flex items-center space-x-2 text-white/70 hover:text-blue-400 transition-colors duration-300 mb-4 md:mb-0"
            >
              <ArrowRight className="w-5 h-5 rotate-180 group-hover:-translate-x-1 transition-transform" />
              <span>Previous: Foundation</span>
            </Link>
            
            <Link 
              to="/"
              className="text-blue-400 font-semibold hover:text-white transition-colors duration-300"
            >
              ⬟ Empire Overview
            </Link>
            
            <Link 
              to="/contracting"
              className="group flex items-center space-x-2 text-white/70 hover:text-blue-400 transition-colors duration-300 mt-4 md:mt-0"
            >
              <span>Next: Contracting</span>
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MediaPage;